import React, { useEffect, useState } from "react";
import moment from "moment";
import { Loader2 } from "lucide-react";

import BottomSheet from "src/components/app/BottomSheet";
import Calendar from "src/components/common/Calendar";
import CgmApis from "src/api/cgm";

interface CGMCalendarBottomSheetProps {
  open: boolean;
  close: () => void;
  data: {
    selectedDate: Date;
    selectedMonth: number;
    selectedYear: number;
  };
  onDateSelect: (date: Date) => void;
}

interface CalendarData {
  monthDates: Array<{ date: string }>;
  sensorCycles: Array<{ start_date: string; end_date: string }>;
}

const CGMCalendarBottomSheet: React.FC<CGMCalendarBottomSheetProps> = ({
  open,
  close,
  data,
  onDateSelect,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(
    data?.selectedDate ?? new Date()
  );
  const [calendarMonth, setCalendarMonth] = useState<number>(
    data?.selectedMonth || moment().month() + 1
  );
  const [calendarYear, setCalendarYear] = useState<number>(
    data?.selectedYear || moment().year()
  );
  const [calendarData, setCalendarData] = useState<{
    isLoading: boolean;
    data: CalendarData | null;
  }>({
    isLoading: false,
    data: null,
  });

  useEffect(() => {
    if (data?.selectedMonth && data?.selectedYear) {
      setCalendarMonth(data.selectedMonth);
      setCalendarYear(data.selectedYear);
    }
  }, [data?.selectedMonth, data?.selectedYear]);

  useEffect(() => {
    if (open && calendarMonth && calendarYear) {
      const fetchCalendar = async () => {
        setCalendarData((prev) => ({ ...prev, isLoading: true, data: null }));
        try {
          const response = await CgmApis.getCGMCalendar({
            month: calendarMonth,
            year: calendarYear,
          });
          const result =
            Array.isArray(response?.data) && response.data.length > 0
              ? response.data[0]
              : response?.data;
          setCalendarData({ isLoading: false, data: result as CalendarData });
        } catch {
          setCalendarData({ isLoading: false, data: null });
        }
      };
      fetchCalendar();
    }
  }, [open, calendarMonth, calendarYear]);

  const handleDateSelect = (date: Date) => {
    if (date && !isNaN(date.getTime())) {
      setSelectedDate(date);
      onDateSelect(date);
    }
  };

  const handleMonthChange = ({
    month,
    year,
  }: {
    month: number;
    year: number;
  }) => {
    setCalendarMonth(month);
    setCalendarYear(year);
  };

  const enabledDateRanges = calendarData.data?.monthDates?.map(({ date }) =>
    moment(date?.split("T")[0] ?? date).format("YYYY-MM-DD")
  );

  return (
    <BottomSheet
      show={open}
      handleClose={close}
      title="Select date"
      showCloseButton
    >
      <div className="relative">
        {calendarData.isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100/60 z-10">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}

        <Calendar
          selectedDate={selectedDate}
          onSelect={handleDateSelect}
          onChangeMonth={handleMonthChange}
          enabledDateRanges={enabledDateRanges}
          className="bg-white"
        />
      </div>
    </BottomSheet>
  );
};

export default CGMCalendarBottomSheet;
